import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useIndicTransliteration } from "../hooks/useIndicTransliteration";
import { Box, TextField, Paper, Chip, Typography, CircularProgress } from "@mui/material";

/**
 * Checks if a string contains only Telugu script characters (U+0C00–U+0C7F)
 */
function isTeluguOnly(str) {
  if (!str) return false;
  return /^[\u0C00-\u0C7F]+$/.test(str);
}

/**
 * Checks if a string contains only Devanagari script characters (U+0900–U+097F)
 */
function isDevanagariOnly(str) {
  if (!str) return false;
  return /^[\u0900-\u097F]+$/.test(str);
}

/**
 * Extracts the latest word from input (text after last space)
 * and its start position in the full input
 */
function getLatestWord(fullText) {
  if (!fullText || typeof fullText !== 'string') return { word: '', startIndex: 0 };
  
  // Find the last space
  const lastSpaceIndex = fullText.lastIndexOf(' ');
  
  // If no space found, the entire text is the current word
  if (lastSpaceIndex === -1) {
    return { word: fullText, startIndex: 0 };
  }
  
  // Extract text after the last space
  const word = fullText.substring(lastSpaceIndex + 1);
  const startIndex = lastSpaceIndex + 1;
  
  return { word, startIndex };
}

/**
 * Replaces the latest word in the full text with a suggestion
 * Preserves all previous words and spacing exactly as entered
 */
function replaceLatestWord(fullText, suggestion) {
  if (!fullText || typeof fullText !== 'string') return suggestion;
  
  const lastSpaceIndex = fullText.lastIndexOf(' ');
  
  // If no space found, replace entire text
  if (lastSpaceIndex === -1) {
    return suggestion;
  }
  
  // Keep everything before and including the last space, replace the word after
  const prefix = fullText.substring(0, lastSpaceIndex + 1);
  return prefix + suggestion;
}

/**
 * TransliteratedInput Component
 * Provides an input field with Telugu and Sanskrit transliteration suggestions
 */
export default function TransliteratedInput({
  label,
  value,
  onChange,
  onSuggestionSelect,
  placeholder,
  fullWidth = true,
  multiline = false,
  minRows = 1,
  targetScript = 'both', // 'telugu', 'devanagari', or 'both'
  // Allow passing extra sx to the TextField (e.g. input line-height)
  inputSx = {},
  onFocus: externalOnFocus,
  onBlur: externalOnBlur,
  disabled = false
}) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [cap, setCap] = useState(50);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const [panelStyle, setPanelStyle] = useState(null);
  const blurTimeoutRef = useRef(null);

  // Extract the latest word (after last space) for transliteration
  const { word: latestWord } = getLatestWord(value);
  const { suggestions, loading } = useIndicTransliteration(latestWord, { cap });

  // Flatten all suggestions into a single array for keyboard navigation
  // Filter to only include pure Indic script characters (no Roman/ISO characters)
  const allSuggestions = React.useMemo(() => {
    const items = [];
    if (targetScript === 'telugu' || targetScript === 'both') {
      if (suggestions.telugu && suggestions.telugu.length > 0) {
        // Filter to only Telugu characters
        const filteredTelugu = suggestions.telugu.filter(s => isTeluguOnly(s));
        items.push(...filteredTelugu);
      }
    }
    if (targetScript === 'devanagari' || targetScript === 'both') {
      if (suggestions.sanskrit && suggestions.sanskrit.length > 0) {
        // Filter to only Devanagari characters
        const filteredSanskrit = suggestions.sanskrit.filter(s => isDevanagariOnly(s));
        items.push(...filteredSanskrit);
      }
    }
    return items;
  }, [suggestions, targetScript]);

  // Reset selected index only when the actual value changes (user types), not on every suggestion update
  const prevValueRef = useRef(value);
  useEffect(() => {
    if (prevValueRef.current !== value) {
      setSelectedIndex(0);
      prevValueRef.current = value;
    }
  }, [value]);

  const handleInputChange = (e) => {
    onChange(e.target.value);
    setShowSuggestions(true);
  };

  const handleSuggestionClick = (suggestion) => {
    // Replace only the latest word while preserving previous words and spacing
    const newValue = replaceLatestWord(value, suggestion);
    if (onSuggestionSelect) {
      onSuggestionSelect(newValue);
    } else {
      onChange(newValue);
    }
    setShowSuggestions(false);
    setSelectedIndex(0);
  };

  const hasSuggestions = (suggestions && (
    ((targetScript === 'telugu' || targetScript === 'both') && suggestions.telugu && suggestions.telugu.some(s => isTeluguOnly(s))) ||
    ((targetScript === 'devanagari' || targetScript === 'both') && suggestions.sanskrit && suggestions.sanskrit.some(s => isDevanagariOnly(s)))
  ));

  const handleKeyDown = (e) => {
    // Clear any pending blur timeout when using keyboard
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }

    if (!showSuggestions || !hasSuggestions || allSuggestions.length === 0) {
      // Allow normal behavior when suggestions aren't shown
      if (e.key === ' ' && value.trim()) {
        setShowSuggestions(true);
        setSelectedIndex(0);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      setSelectedIndex((prev) => (prev + 1) % allSuggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      setSelectedIndex((prev) => (prev - 1 + allSuggestions.length) % allSuggestions.length);
    } else if (e.key === 'Enter' && selectedIndex >= 0 && selectedIndex < allSuggestions.length) {
      e.preventDefault();
      e.stopPropagation();
      handleSuggestionClick(allSuggestions[selectedIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setShowSuggestions(false);
      setSelectedIndex(0);
    }
  };

  const handleBlur = () => {
    blurTimeoutRef.current = setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
    // Call external onBlur handler if provided
    if (externalOnBlur) externalOnBlur();
  };

  const handleFocus = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setShowSuggestions(true);
    // Call external onFocus handler if provided
    if (externalOnFocus) externalOnFocus();
  };

  // Update panel position to align with the input box; this avoids clipping by parent containers
  useEffect(() => {
    function update() {
      if (!wrapperRef.current) return;
      const rect = wrapperRef.current.getBoundingClientRect();
      setPanelStyle({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
    if (showSuggestions && hasSuggestions) update();
    const onChange = () => { if (showSuggestions && hasSuggestions) update(); };
    window.addEventListener('resize', onChange);
    window.addEventListener('scroll', onChange, true);
    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('scroll', onChange, true);
    };
  }, [showSuggestions, hasSuggestions]); // Removed 'suggestions' - it changes on every render

  // Render suggestions into a body-level portal so they aren't clipped by overflow/stacking contexts
  const renderPanel = () => {
    if (!panelStyle) return null;
    return createPortal(
      <Paper
        elevation={3}
        style={{
          position: 'absolute',
          top: panelStyle.top,
          left: panelStyle.left,
          width: panelStyle.width,
          zIndex: 1400,
          maxHeight: 300,
          overflowY: 'auto',
          backgroundColor: '#f9f9f9',
          borderRadius: 8,
          padding: 12
        }}
      >
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!loading && (
          <>
            {/* Suggestions */}

            {(targetScript === 'telugu' || targetScript === 'both') && suggestions.telugu && suggestions.telugu.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: '#666', fontWeight: 600 }}>
                  🇮🇳 Telugu
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                  {suggestions.telugu.filter(s => isTeluguOnly(s)).map((suggestion, idx) => {
                    const globalIdx = allSuggestions.indexOf(suggestion);
                    return (
                      <Chip
                        key={`telugu-${idx}`}
                        label={suggestion}
                        onClick={() => handleSuggestionClick(suggestion)}
                        variant={globalIdx === selectedIndex ? "filled" : "outlined"}
                        color={globalIdx === selectedIndex ? "primary" : "default"}
                        size="small"
                        sx={{ 
                          cursor: 'pointer', 
                          '&:hover': { backgroundColor: '#e8f5e9' },
                          ...(globalIdx === selectedIndex && {
                            backgroundColor: '#1976d2',
                            color: 'white',
                            fontWeight: 600
                          })
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}

            {(targetScript === 'devanagari' || targetScript === 'both') && suggestions.sanskrit && suggestions.sanskrit.length > 0 && (
              <Box>
                <Typography variant="caption" sx={{ color: '#666', fontWeight: 600 }}>
                  🇮🇳 Sanskrit (Devanagari)
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                  {suggestions.sanskrit.filter(s => isDevanagariOnly(s)).map((suggestion, idx) => {
                    const globalIdx = allSuggestions.indexOf(suggestion);
                    return (
                      <Chip
                        key={`sanskrit-${idx}`}
                        label={suggestion}
                        onClick={() => handleSuggestionClick(suggestion)}
                        variant={globalIdx === selectedIndex ? "filled" : "outlined"}
                        color={globalIdx === selectedIndex ? "primary" : "default"}
                        size="small"
                        sx={{ 
                          cursor: 'pointer', 
                          '&:hover': { backgroundColor: '#e3f2fd' },
                          ...(globalIdx === selectedIndex && {
                            backgroundColor: '#1976d2',
                            color: 'white',
                            fontWeight: 600
                          })
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}
          </>
        )}
      </Paper>,
      document.body
    );
  };

  return (
    <Box ref={wrapperRef} sx={{ position: "relative", mb: 2 }}>
      <TextField
        inputRef={inputRef}
        label={label}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        fullWidth={fullWidth}
        multiline={multiline}
        minRows={minRows}
        variant="outlined"
        disabled={disabled}
        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 }, ...inputSx }}
      />
      {showSuggestions && hasSuggestions && renderPanel()}
    </Box>
  );
  }
